import { Activity, MapPin, Syringe, TriangleAlert } from "lucide-react";

/**
 * The FARE-shaped emergency care plan for a single allergy, rendered in a fixed
 * order: symptoms → treatment → device → location. Shared by the owner list,
 * the caregiver view, and the printed Emergency Information Form so all three
 * read identically — a caregiver who learned the layout in one sees the same
 * thing everywhere.
 *
 * Deliberately NOT wrapped in Reveal or any scroll-triggered animation: this
 * content must survive printing (see the warning in components/ui/Reveal.tsx).
 *
 * The props are the narrow intersection of AllergySummary (owner) and
 * ScopedAllergy (caregiver), so both callers satisfy it without a mapper.
 */
export interface AllergyPlanPanelProps {
  reactionSymptoms: string | null;
  treatment: string | null;
  epinephrineLocation: string | null;
  /**
   * Undefined = the medications section wasn't shared on this link, so we say
   * nothing. Null = shared, but nothing is linked — worth flagging on a severe
   * allergy. The distinction is why this is optional rather than nullable.
   */
  rescueMedicationName?: string | null;
  severity: "mild" | "moderate" | "severe";
}

function PlanRow({
  icon: Icon,
  label,
  children,
  emphasis = false,
}: {
  icon: typeof Activity;
  label: string;
  children: React.ReactNode;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon
        className={`mt-0.5 h-4 w-4 flex-shrink-0 ${emphasis ? "text-red-600" : "text-neutral-500"}`}
        aria-hidden
      />
      <p className="text-xs leading-relaxed">
        <span
          className={`font-semibold ${emphasis ? "text-red-800" : "text-neutral-700"}`}
        >
          {label}:{" "}
        </span>
        <span className="text-neutral-800">{children}</span>
      </p>
    </div>
  );
}

export function AllergyPlanPanel({
  reactionSymptoms,
  treatment,
  epinephrineLocation,
  rescueMedicationName,
  severity,
}: AllergyPlanPanelProps) {
  const isSevere = severity === "severe";
  // `undefined` means the section wasn't shared — never render a missing-device
  // warning from an absence we simply can't see.
  const missingDevice = isSevere && rescueMedicationName === null;
  const hasPlan =
    reactionSymptoms || treatment || epinephrineLocation || rescueMedicationName;

  if (!hasPlan && !missingDevice) return null;

  return (
    <div className="mt-2 space-y-1.5 rounded-lg bg-white/70 p-2.5">
      {reactionSymptoms && (
        <PlanRow icon={Activity} label="Signs of a reaction">
          {reactionSymptoms}
        </PlanRow>
      )}
      {treatment && (
        <PlanRow icon={TriangleAlert} label="Treatment" emphasis={isSevere}>
          {treatment}
        </PlanRow>
      )}
      {rescueMedicationName && (
        <PlanRow icon={Syringe} label="Rescue medication" emphasis={isSevere}>
          {rescueMedicationName}
        </PlanRow>
      )}
      {epinephrineLocation && (
        <PlanRow icon={MapPin} label="Kept">
          {epinephrineLocation}
        </PlanRow>
      )}
      {missingDevice && (
        <p className="text-xs font-medium text-red-700">
          No rescue medication is linked to this severe allergy.
        </p>
      )}
    </div>
  );
}
